<script>
    import tagService from "../../../../../tag/service.js";
    import Spinner from "../../../../../../components/spinner.svelte";
    import AssetList from "../../../../../asset/_components/list.svelte";
    import { assets } from "../../../../../asset/assets.state.svelte.js";
    import BreadCrumbNav from "../../../../../../components/breadcrum-nav.svelte";
    import { truncateString } from "../../../../../../js/utils";
    import { searchAssets } from "../../../../../asset/assets.state.svelte.js";
    import { assets_kinds } from "../../../../../asset/assets.api.js";
    import { params, goto } from "@roxi/routify";
    import { format } from "svelte-i18n";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    const redirect = $goto;

    let kind = $derived($params.kind ? decodeURIComponent($params.kind) : "");
    let name = $derived($params.name ? decodeURIComponent($params.name) : "");
    let value = $derived($params.value ? decodeURIComponent($params.value) : "");

    // Active filters driven strictly by the URL
    let currentFilter = $derived.by(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));
        const active = {};
        if (paramsCopy.asset__kind__in) active.asset__kind__in = paramsCopy.asset__kind__in;
        if (paramsCopy.asset_tag_id) active.asset_tag_id = paramsCopy.asset_tag_id;
        return active;
    });

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        asset_tag_id: ""
    });

    // Localize asset kinds for TomSelect
    let translatedAssetKinds = $derived(
        assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));

        let initialKind = paramsCopy.asset__kind__in || [];
        if (typeof initialKind === "string") {
            initialKind = initialKind.split(",");
        }

        filters = {
            asset__kind__in: initialKind,
            asset_tag_id: paramsCopy.asset_tag_id || ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    const handleEmptyFilters = (filtersObj = {}) => {
        const clean = {};
        for (let [key, val] of Object.entries(filtersObj)) {
            if (val !== "" && val !== null && val !== undefined) {
                clean[key] = val;
            }
        }
        return clean;
    };

    const handleAssetFilters = () => {
        let returnValue = {
            property_kind: kind,
            property_name: name,
            property_value: value,
            include_finding_count: true,
        };
        if (currentFilter.asset_tag_id) {
            returnValue.tag_id = currentFilter.asset_tag_id;
        }
        if (currentFilter.asset__kind__in) {
            returnValue.kind__in = currentFilter.asset__kind__in;
        }
        return returnValue;
    };

    let assetFilters = $derived.by(() => handleAssetFilters());

    $effect(() => {
        searchAssets("v2/asset/", {
            buildList: { tags: { elements: [], associations: [] } },
            filter: assetFilters,
        });
    });

    const getTags = async () => await tagService.search("v2/tag/");

    function handleFilterChange() {
        let updatedFilter = {};

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        }

        if (filters.asset_tag_id) {
            updatedFilter.asset_tag_id = filters.asset_tag_id;
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);
        redirect("./", { kind: kind, name: name, value: value, ...cleanFilters });
    }

    const clearFilters = () => {
        if (!currentFilter.asset__kind__in && !currentFilter.asset_tag_id) {
            return;
        }

        filters = { asset__kind__in: [], asset_tag_id: "" };
        redirect("./", { kind: kind, name: name, value: value });
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1
                    title={`${kind == name ? "" : kind + " - "} ${name} - ${value || ""}`}
                >
                    {kind == name ? "" : kind + " - "}
                    {name} - {truncateString(value, 50)}
                </h1>
            </div>
            <div class="col-6">
                <BreadCrumbNav
                    routes={[
                        {
                            label: $format("page.properties.asset_props"),
                            path: "/property/asset",
                        },
                        {
                            label: kind,
                            path: "/property/asset/[kind]",
                            params: { kind: kind }
                        },
                        {
                            label: name,
                            path: "/property/asset/[kind]/[name]",
                            params: { kind: kind, name: name }
                        },
                        { label: truncateString(value, 50) },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="row">
        <div class="col-12 d-flex w-100">
            <div class="card mt-3 card-secondary card-outline no-export w-100">
                <div class="card-header">
                    <h4 class="card-title">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    <div class="row">
                        <div class="col-12 col-md-6 mb-3">
                            <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("components.advancedSearch.asset_kind")}
                            </label>

                            <!-- SVELTE 5 UPDATE: Replaced Select2 with TomSelect -->
                            <select
                                id="asset__kind__in"
                                name="asset__kind__in"
                                multiple
                                use:tomSelectAction={{
                                    options: translatedAssetKinds,
                                    value: filters.asset__kind__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.asset__kind__in = val),
                                }}
                            ></select>
                        </div>

                        <div class="col-12 col-md-6 mb-3">
                            {#await getTags()}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>
                                <select class="form-select" name="asset_tag_id" disabled>
                                    <option value="">--</option>
                                </select>
                            {:then tags}
                                <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_tag")}
                                </label>

                                <!-- SVELTE 5 UPDATE: Standard BS5 select -->
                                <select
                                    class="form-select"
                                    name="asset_tag_id"
                                    bind:value={filters.asset_tag_id}
                                >
                                    <option value="">--</option>
                                    {#each tags as opt}
                                        <option value={opt.id}>
                                            {opt.name}
                                        </option>
                                    {/each}
                                </select>
                            {/await}
                        </div>
                    </div>

                    <div class="btn-strip pt-3 mt-2 border-top">
                        <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                            <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                        </button>
                        <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                            <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row mt-3">
        <div class="col-12">
            {#if assets.list.loading}
                <div class="d-flex justify-content-center"><Spinner /></div>
            {:else}
                <AssetList kind={"all"} showActions={false} />
            {/if}
        </div>
    </div>
</section>

<style>
    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>